import axios, { AxiosInstance } from "axios";
import { API } from "../queries/api";
import { resolveMatrixMode } from "./matrixMode";

/**
 * MatrixClient — Dual-mode Matrix API wrapper
 *
 * Mode 1 (backend):  Proxies through Java backend  /api/v1/matrix/*
 * Mode 2 (direct):   Talks directly to Synapse homeserver
 *
 * Set REACT_APP_MATRIX_MODE=direct  and
 *     REACT_APP_MATRIX_HOMESERVER=http://localhost:8008
 * to enable direct mode for local development.
 */

// ==================== TYPES ====================

export interface MatrixTokenResponse {
  accessToken: string;
  matrixUserId: string;
  homeServer: string;
}

export interface CreateMatrixRoomParams {
  clubId?: string;
  name: string;
  description?: string;
  image?: string;
  memberIds?: string[];
  includeAll?: boolean;
  chatPurpose?: string;
}

export interface MatrixRoomInfo {
  id: string;
  bridgesChatId?: string;
  name: string;
  description?: string;
  image?: string;
  memberCount?: number;
  unreadCount?: number;
  lastMessage?: MatrixMessage;
  chatPurpose?: string;
  clubId?: string;
}

export interface MatrixMessage {
  eventId: string;
  roomId: string;
  sender: string;
  senderDisplayName?: string;
  senderAvatar?: string;
  content: string;
  timestamp: number;
  type: string;
  edited?: boolean;
  redacted?: boolean;
  replyTo?: string;
  replyToContent?: string;
  replyToSender?: string;
  editedContent?: string;
  reactions?: Record<string, string[]>; // emoji → userIds[]
  pinned?: boolean;
  attachments?: MatrixAttachment[];
  msgtype?: string;
}

export interface MatrixAttachment {
  url: string;
  filename: string;
  mimetype: string;
  size?: number;
  width?: number;
  height?: number;
  thumbnailUrl?: string;
}

export interface MatrixMember {
  userId: string;
  displayName?: string;
  avatarUrl?: string;
  membership: string;
}

export interface MatrixMemberProfile {
  matrixUserId: string;
  bridgesUserId: string;
  firstName?: string;
  lastName?: string;
  displayName: string;
  imageLink?: string;
  membership: string; // "join", "ban", "invite", "leave"
}

export interface MatrixUnreadCounts {
  [roomId: string]: number;
}

export interface MatrixSyncResponse {
  nextBatch: string;
  rooms: {
    join: Record<string, MatrixJoinedRoom>;
    invite: Record<string, any>;
    leave: Record<string, any>;
  };
}

export interface MatrixJoinedRoom {
  timeline: {
    events: MatrixRawEvent[];
    prevBatch?: string;
    limited?: boolean;
  };
  state: { events: MatrixRawEvent[] };
  ephemeral: { events: MatrixRawEvent[] };
  unreadNotifications?: {
    notification_count?: number;
    highlight_count?: number;
  };
}

export interface MatrixRawEvent {
  type: string;
  event_id?: string;
  sender?: string;
  origin_server_ts?: number;
  content?: any;
  state_key?: string;
  unsigned?: any;
  room_id?: string;
}

export type MessageCallback = (message: MatrixMessage) => void;

// ==================== INTERFACE ====================

export interface IMatrixClient {
  readonly mode: "direct" | "backend";

  // Auth
  login(user: string, password: string): Promise<MatrixTokenResponse>;
  logout(): void;
  isLoggedIn(): boolean;
  getUserId(): string | null;
  getAccessToken(): string | null;

  // Rooms
  createRoom(params: {
    name: string;
    description?: string;
    image?: string;
    memberIds?: string[];
    preset?: string;
    visibility?: string;
  }): Promise<string>;
  createClubChat(params: CreateMatrixRoomParams): Promise<string>;
  getRoomInfo(roomId: string): Promise<MatrixRoomInfo>;
  deleteRoom(roomId: string): Promise<void>;
  updateRoomName(roomId: string, name: string): Promise<void>;
  updateRoomDescription(roomId: string, description: string): Promise<void>;
  updateRoomAvatar(roomId: string, imageUrl: string): Promise<void>;
  getClubChats(clubId: string): Promise<MatrixRoomInfo[]>;
  getPrivateChats(): Promise<MatrixRoomInfo[]>;
  getJoinedRooms(): Promise<MatrixRoomInfo[]>;

  // Members
  addMember(roomId: string, userId: string): Promise<void>;
  removeMember(roomId: string, userId: string): Promise<void>;
  getMembers(roomId: string): Promise<MatrixMember[]>;
  getMemberProfiles(roomId: string): Promise<MatrixMemberProfile[]>;
  banUser(roomId: string, userId: string, reason?: string): Promise<void>;
  unbanUser(roomId: string, userId: string): Promise<void>;

  // Messages
  sendMessage(roomId: string, content: string): Promise<string>;
  editMessage(roomId: string, eventId: string, newContent: string): Promise<string>;
  deleteMessage(roomId: string, eventId: string, reason?: string): Promise<string>;
  getMessages(roomId: string, limit?: number, from?: string): Promise<{
    messages: MatrixMessage[];
    end?: string;
  }>;

  // Replies
  sendReply(roomId: string, parentEventId: string, content: string): Promise<string>;

  // Reactions
  sendReaction(roomId: string, eventId: string, emoji: string): Promise<string>;
  removeReaction(roomId: string, reactionEventId: string): Promise<string>;

  // Pins
  pinMessage(roomId: string, eventId: string): Promise<void>;
  unpinMessage(roomId: string, eventId: string): Promise<void>;
  getPinnedMessages(roomId: string): Promise<string[]>;

  // File uploads
  uploadFile(file: File): Promise<string>; // returns mxc:// URI
  sendFileMessage(roomId: string, file: File, mxcUrl: string): Promise<string>;
  getMediaUrl(mxcUrl: string): string;

  // Forward
  forwardMessage(fromRoomId: string, toRoomId: string, content: string): Promise<string>;

  // Real-time
  sync(since?: string, timeout?: number): Promise<MatrixSyncResponse>;
  sendTypingIndicator(roomId: string, isTyping: boolean, timeout?: number): Promise<void>;
  sendReadReceipt(roomId: string, eventId: string): Promise<void>;
  getUnreadCounts(): Promise<MatrixUnreadCounts>;

  // Status
  getStatus(): Promise<any>;
}

// ==================== SYNAPSE DIRECT CLIENT ====================

let txnCounter = 0;
function nextTxnId(): string {
  return `m${Date.now()}_${++txnCounter}`;
}

class SynapseDirectClient implements IMatrixClient {
  readonly mode = "direct" as const;
  private homeserverUrl: string;
  private http: AxiosInstance;
  private accessToken: string | null = null;
  private userId: string | null = null;

  constructor(homeserverUrl: string) {
    this.homeserverUrl = homeserverUrl.replace(/\/$/, "");
    this.http = axios.create({ baseURL: this.homeserverUrl });

    // Restore from localStorage
    const stored = localStorage.getItem("matrix_auth");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        this.accessToken = parsed.accessToken || null;
        this.userId = parsed.userId || null;
        this.applyAuth();
      } catch {
        /* ignore */
      }
    }
  }

  private applyAuth() {
    if (this.accessToken) {
      this.http.defaults.headers.common["Authorization"] = `Bearer ${this.accessToken}`;
    } else {
      delete this.http.defaults.headers.common["Authorization"];
    }
  }

  private persistAuth() {
    if (this.accessToken && this.userId) {
      localStorage.setItem(
        "matrix_auth",
        JSON.stringify({ accessToken: this.accessToken, userId: this.userId })
      );
    } else {
      localStorage.removeItem("matrix_auth");
    }
  }

  // ── Auth ─────────────────────────────────────

  async login(user: string, password: string): Promise<MatrixTokenResponse> {
    const { data } = await this.http.post("/_matrix/client/v3/login", {
      type: "m.login.password",
      identifier: { type: "m.id.user", user },
      password,
    });
    this.accessToken = data.access_token;
    this.userId = data.user_id;
    this.applyAuth();
    this.persistAuth();
    return {
      accessToken: data.access_token,
      matrixUserId: data.user_id,
      homeServer: data.home_server || this.homeserverUrl,
    };
  }

  logout(): void {
    this.accessToken = null;
    this.userId = null;
    this.applyAuth();
    this.persistAuth();
  }

  isLoggedIn(): boolean {
    return !!this.accessToken;
  }

  getUserId(): string | null {
    return this.userId;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  // ── Rooms ────────────────────────────────────

  async createRoom(params: {
    name: string;
    description?: string;
    image?: string;
    memberIds?: string[];
    preset?: string;
    visibility?: string;
  }): Promise<string> {
    const body: any = {
      name: params.name,
      preset: params.preset || "private_chat",
      visibility: params.visibility || "private",
    };
    if (params.description) {
      body.topic = params.description;
    }
    if (params.memberIds?.length) {
      body.invite = params.memberIds.map((id) =>
        id.startsWith("@") ? id : `@${id}:${this.getServerName()}`
      );
    }
    const initState: any[] = [];
    if (params.image) {
      initState.push({
        type: "m.room.avatar",
        state_key: "",
        content: { url: params.image },
      });
    }
    if (initState.length) body.initial_state = initState;

    const { data } = await this.http.post("/_matrix/client/v3/createRoom", body);
    return data.room_id;
  }

  async createClubChat(params: CreateMatrixRoomParams): Promise<string> {
    return this.createRoom({
      name: params.name,
      description: params.description,
      image: params.image,
      memberIds: params.memberIds,
    });
  }

  async getRoomInfo(roomId: string): Promise<MatrixRoomInfo> {
    const encodedRoomId = encodeURIComponent(roomId);
    const info: MatrixRoomInfo = { id: roomId, name: roomId };

    try {
      const { data: stateEvents } = await this.http.get(
        `/_matrix/client/v3/rooms/${encodedRoomId}/state`
      );

      for (const ev of stateEvents) {
        if (ev.type === "m.room.name") info.name = ev.content?.name || roomId;
        if (ev.type === "m.room.topic") info.description = ev.content?.topic;
        if (ev.type === "m.room.avatar") info.image = ev.content?.url;
        if (ev.type === "m.room.member" && ev.content?.membership === "join") {
          info.memberCount = (info.memberCount || 0) + 1;
        }
      }
    } catch (err) {
      console.warn("getRoomInfo state error:", err);
    }

    return info;
  }

  async deleteRoom(roomId: string): Promise<void> {
    const encodedRoomId = encodeURIComponent(roomId);
    try {
      await this.http.delete(
        `/_synapse/admin/v1/rooms/${encodedRoomId}`,
        { data: { purge: true } }
      );
    } catch {
      await this.http.post(
        `/_matrix/client/v3/rooms/${encodedRoomId}/leave`,
        {}
      );
    }
  }

  async updateRoomName(roomId: string, name: string): Promise<void> {
    const encodedRoomId = encodeURIComponent(roomId);
    await this.http.put(
      `/_matrix/client/v3/rooms/${encodedRoomId}/state/m.room.name/`,
      { name }
    );
  }

  async updateRoomDescription(roomId: string, description: string): Promise<void> {
    const encodedRoomId = encodeURIComponent(roomId);
    await this.http.put(
      `/_matrix/client/v3/rooms/${encodedRoomId}/state/m.room.topic/`,
      { topic: description }
    );
  }

  async updateRoomAvatar(roomId: string, imageUrl: string): Promise<void> {
    const encodedRoomId = encodeURIComponent(roomId);
    await this.http.put(
      `/_matrix/client/v3/rooms/${encodedRoomId}/state/m.room.avatar/`,
      { url: imageUrl }
    );
  }

  async getPrivateChats(): Promise<MatrixRoomInfo[]> {
    // In direct mode, not supported — return empty
    return [];
  }

  async getClubChats(clubId: string): Promise<MatrixRoomInfo[]> {
    // In direct mode, fetch all rooms then filter by club metadata
    const allRooms = await this.getJoinedRooms();
    if (!clubId) return allRooms;

    // Try to filter rooms that have matching clubId in their state
    const filtered: MatrixRoomInfo[] = [];
    for (const room of allRooms) {
      try {
        const encodedRoomId = encodeURIComponent(room.id);
        const { data: stateEvents } = await this.http.get(
          `/_matrix/client/v3/rooms/${encodedRoomId}/state`
        );
        const metadata = stateEvents.find(
          (ev: any) => ev.type === "com.bridges.room_metadata"
        );
        if (metadata?.content?.bridges_club_id === clubId) {
          room.chatPurpose = metadata.content.bridges_room_type;
          room.clubId = clubId;
          filtered.push(room);
        }
      } catch {
        // If we can't read state, include room anyway (might be a simple room)
      }
    }
    // If no rooms match metadata (e.g. metadata not set), return all as fallback
    return filtered.length > 0 ? filtered : allRooms;
  }

  async getJoinedRooms(): Promise<MatrixRoomInfo[]> {
    const { data } = await this.http.get("/_matrix/client/v3/joined_rooms");
    const roomIds: string[] = data.joined_rooms || [];

    const rooms: MatrixRoomInfo[] = [];
    for (const rid of roomIds) {
      try {
        const info = await this.getRoomInfo(rid);
        rooms.push(info);
      } catch {
        rooms.push({ id: rid, name: rid });
      }
    }
    return rooms;
  }

  // ── Members ──────────────────────────────────

  async addMember(roomId: string, userId: string): Promise<void> {
    const encodedRoomId = encodeURIComponent(roomId);
    const matrixUserId = userId.startsWith("@")
      ? userId
      : `@${userId}:${this.getServerName()}`;
    await this.http.post(
      `/_matrix/client/v3/rooms/${encodedRoomId}/invite`,
      { user_id: matrixUserId }
    );
  }

  async removeMember(roomId: string, userId: string): Promise<void> {
    const encodedRoomId = encodeURIComponent(roomId);
    const matrixUserId = userId.startsWith("@")
      ? userId
      : `@${userId}:${this.getServerName()}`;
    await this.http.post(
      `/_matrix/client/v3/rooms/${encodedRoomId}/kick`,
      { user_id: matrixUserId, reason: "Removed by admin" }
    );
  }

  async getMembers(roomId: string): Promise<MatrixMember[]> {
    const encodedRoomId = encodeURIComponent(roomId);
    const { data } = await this.http.get(
      `/_matrix/client/v3/rooms/${encodedRoomId}/members`
    );
    return (data.chunk || [])
      .filter((ev: any) => ev.content?.membership === "join")
      .map((ev: any) => ({
        userId: ev.state_key,
        displayName: ev.content?.displayname,
        avatarUrl: ev.content?.avatar_url,
        membership: ev.content?.membership,
      }));
  }

  async getMemberProfiles(roomId: string): Promise<MatrixMemberProfile[]> {
    const members = await this.getMembers(roomId);
    return members.map((m) => ({
      matrixUserId: m.userId,
      bridgesUserId: "",
      displayName: m.displayName || m.userId,
      imageLink: m.avatarUrl,
      membership: m.membership,
    }));
  }

  async banUser(roomId: string, userId: string, reason?: string): Promise<void> {
    const encodedRoomId = encodeURIComponent(roomId);
    const matrixUserId = userId.startsWith("@")
      ? userId
      : `@${userId}:${this.getServerName()}`;
    await this.http.post(
      `/_matrix/client/v3/rooms/${encodedRoomId}/ban`,
      { user_id: matrixUserId, reason }
    );
  }

  async unbanUser(roomId: string, userId: string): Promise<void> {
    const encodedRoomId = encodeURIComponent(roomId);
    const matrixUserId = userId.startsWith("@")
      ? userId
      : `@${userId}:${this.getServerName()}`;
    await this.http.post(
      `/_matrix/client/v3/rooms/${encodedRoomId}/unban`,
      { user_id: matrixUserId }
    );
  }

  // ── Messages ─────────────────────────────────

  async sendMessage(roomId: string, content: string): Promise<string> {
    const encodedRoomId = encodeURIComponent(roomId);
    const txnId = nextTxnId();
    const { data } = await this.http.put(
      `/_matrix/client/v3/rooms/${encodedRoomId}/send/m.room.message/${txnId}`,
      { msgtype: "m.text", body: content }
    );
    return data.event_id;
  }

  async editMessage(roomId: string, eventId: string, newContent: string): Promise<string> {
    const encodedRoomId = encodeURIComponent(roomId);
    const txnId = nextTxnId();
    const { data } = await this.http.put(
      `/_matrix/client/v3/rooms/${encodedRoomId}/send/m.room.message/${txnId}`,
      {
        msgtype: "m.text",
        body: `* ${newContent}`,
        "m.new_content": {
          msgtype: "m.text",
          body: newContent,
        },
        "m.relates_to": {
          rel_type: "m.replace",
          event_id: eventId,
        },
      }
    );
    return data.event_id;
  }

  async deleteMessage(roomId: string, eventId: string, reason?: string): Promise<string> {
    const encodedRoomId = encodeURIComponent(roomId);
    const txnId = nextTxnId();
    const body: any = {};
    if (reason) body.reason = reason;
    const { data } = await this.http.put(
      `/_matrix/client/v3/rooms/${encodedRoomId}/redact/${encodeURIComponent(eventId)}/${txnId}`,
      body
    );
    return data.event_id;
  }

  async getMessages(
    roomId: string,
    limit: number = 50,
    from?: string
  ): Promise<{ messages: MatrixMessage[]; end?: string }> {
    const encodedRoomId = encodeURIComponent(roomId);
    const params: any = { dir: "b", limit };
    if (from) params.from = from;

    const { data } = await this.http.get(
      `/_matrix/client/v3/rooms/${encodedRoomId}/messages`,
      { params }
    );

    const messages: MatrixMessage[] = (data.chunk || [])
      .filter((ev: any) => ev.type === "m.room.message")
      .map((ev: any) => this.rawEventToMessage(ev, roomId))
      .reverse(); // oldest first

    return { messages, end: data.end };
  }

  // ── Replies ──────────────────────────────────

  async sendReply(roomId: string, parentEventId: string, content: string): Promise<string> {
    const encodedRoomId = encodeURIComponent(roomId);
    const txnId = nextTxnId();
    const { data } = await this.http.put(
      `/_matrix/client/v3/rooms/${encodedRoomId}/send/m.room.message/${txnId}`,
      {
        msgtype: "m.text",
        body: content,
        "m.relates_to": {
          "m.in_reply_to": {
            event_id: parentEventId,
          },
        },
      }
    );
    return data.event_id;
  }

  // ── Reactions ────────────────────────────────

  async sendReaction(roomId: string, eventId: string, emoji: string): Promise<string> {
    const encodedRoomId = encodeURIComponent(roomId);
    const txnId = nextTxnId();
    const { data } = await this.http.put(
      `/_matrix/client/v3/rooms/${encodedRoomId}/send/m.reaction/${txnId}`,
      {
        "m.relates_to": {
          rel_type: "m.annotation",
          event_id: eventId,
          key: emoji,
        },
      }
    );
    return data.event_id;
  }

  async removeReaction(roomId: string, reactionEventId: string): Promise<string> {
    const encodedRoomId = encodeURIComponent(roomId);
    const txnId = nextTxnId();
    const { data } = await this.http.put(
      `/_matrix/client/v3/rooms/${encodedRoomId}/redact/${encodeURIComponent(reactionEventId)}/${txnId}`,
      {}
    );
    return data.event_id;
  }

  // ── Pins ─────────────────────────────────────

  async pinMessage(roomId: string, eventId: string): Promise<void> {
    const encodedRoomId = encodeURIComponent(roomId);
    const currentPins = await this.getPinnedMessages(roomId);
    if (!currentPins.includes(eventId)) {
      currentPins.push(eventId);
    }
    await this.http.put(
      `/_matrix/client/v3/rooms/${encodedRoomId}/state/m.room.pinned_events/`,
      { pinned: currentPins }
    );
  }

  async unpinMessage(roomId: string, eventId: string): Promise<void> {
    const encodedRoomId = encodeURIComponent(roomId);
    const currentPins = await this.getPinnedMessages(roomId);
    const newPins = currentPins.filter((id) => id !== eventId);
    await this.http.put(
      `/_matrix/client/v3/rooms/${encodedRoomId}/state/m.room.pinned_events/`,
      { pinned: newPins }
    );
  }

  async getPinnedMessages(roomId: string): Promise<string[]> {
    const encodedRoomId = encodeURIComponent(roomId);
    try {
      const { data } = await this.http.get(
        `/_matrix/client/v3/rooms/${encodedRoomId}/state/m.room.pinned_events/`
      );
      return data.pinned || [];
    } catch {
      return [];
    }
  }

  // ── File Upload ──────────────────────────────

  async uploadFile(file: File): Promise<string> {
    const { data } = await this.http.post(
      `/_matrix/media/v3/upload?filename=${encodeURIComponent(file.name)}`,
      file,
      {
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
      }
    );
    return data.content_uri; // mxc:// URL
  }

  async sendFileMessage(roomId: string, file: File, mxcUrl: string): Promise<string> {
    const encodedRoomId = encodeURIComponent(roomId);
    const txnId = nextTxnId();

    let msgtype = "m.file";
    if (file.type.startsWith("image/")) msgtype = "m.image";
    else if (file.type.startsWith("video/")) msgtype = "m.video";
    else if (file.type.startsWith("audio/")) msgtype = "m.audio";

    const messageContent: any = {
      msgtype,
      body: file.name,
      url: mxcUrl,
      info: {
        mimetype: file.type,
        size: file.size,
      },
    };

    // For images, try to get dimensions
    if (msgtype === "m.image") {
      try {
        const dims = await this.getImageDimensions(file);
        messageContent.info.w = dims.width;
        messageContent.info.h = dims.height;
      } catch {
        // non-critical
      }
    }

    const { data } = await this.http.put(
      `/_matrix/client/v3/rooms/${encodedRoomId}/send/m.room.message/${txnId}`,
      messageContent
    );
    return data.event_id;
  }

  getMediaUrl(mxcUrl: string): string {
    if (!mxcUrl || !mxcUrl.startsWith("mxc://")) return mxcUrl;
    const parts = mxcUrl.replace("mxc://", "").split("/");
    const serverName = parts[0];
    const mediaId = parts.slice(1).join("/");
    return `${this.homeserverUrl}/_matrix/media/v3/download/${serverName}/${mediaId}`;
  }

  private getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  }

  // ── Forward ──────────────────────────────────

  async forwardMessage(_fromRoomId: string, toRoomId: string, content: string): Promise<string> {
    return this.sendMessage(toRoomId, content);
  }

  // ── Real-time ────────────────────────────────

  async sync(since?: string, timeout: number = 30000): Promise<MatrixSyncResponse> {
    const params: any = { timeout };
    if (since) params.since = since;
    params.filter = JSON.stringify({
      room: {
        timeline: { limit: 50 },
        state: { lazy_load_members: true },
      },
    });

    const { data } = await this.http.get("/_matrix/client/v3/sync", {
      params,
      timeout: timeout + 10000,
    });

    const rooms: MatrixSyncResponse["rooms"] = {
      join: {},
      invite: {},
      leave: {},
    };

    if (data.rooms?.join) {
      for (const [rid, roomData] of Object.entries(data.rooms.join as Record<string, any>)) {
        rooms.join[rid] = {
          timeline: {
            events: roomData.timeline?.events || [],
            prevBatch: roomData.timeline?.prev_batch,
            limited: roomData.timeline?.limited,
          },
          state: { events: roomData.state?.events || [] },
          ephemeral: { events: roomData.ephemeral?.events || [] },
          unreadNotifications: roomData.unread_notifications,
        };
      }
    }
    if (data.rooms?.invite) rooms.invite = data.rooms.invite;
    if (data.rooms?.leave) rooms.leave = data.rooms.leave;

    return {
      nextBatch: data.next_batch,
      rooms,
    };
  }

  async sendTypingIndicator(roomId: string, isTyping: boolean, timeout: number = 4000): Promise<void> {
    const encodedRoomId = encodeURIComponent(roomId);
    const encodedUserId = encodeURIComponent(this.userId || "");
    try {
      await this.http.put(
        `/_matrix/client/v3/rooms/${encodedRoomId}/typing/${encodedUserId}`,
        { typing: isTyping, timeout }
      );
    } catch {
      // Non-critical
    }
  }

  async sendReadReceipt(roomId: string, eventId: string): Promise<void> {
    const encodedRoomId = encodeURIComponent(roomId);
    const encodedEventId = encodeURIComponent(eventId);
    try {
      await this.http.post(
        `/_matrix/client/v3/rooms/${encodedRoomId}/receipt/m.read/${encodedEventId}`,
        {}
      );
    } catch {
      // Non-critical
    }
  }

  async getUnreadCounts(): Promise<MatrixUnreadCounts> {
    return {};
  }

  // ── Status ───────────────────────────────────

  async getStatus(): Promise<any> {
    const { data } = await this.http.get("/_matrix/client/versions");
    return { connected: true, versions: data.versions, homeserver: this.homeserverUrl };
  }

  // ── Helpers ──────────────────────────────────

  private getServerName(): string {
    try {
      const url = new URL(this.homeserverUrl);
      return url.hostname + (url.port ? `:${url.port}` : "");
    } catch {
      return "localhost";
    }
  }

  rawEventToMessage(ev: any, roomId?: string): MatrixMessage {
    const content = ev.content || {};
    const isEdit = content["m.relates_to"]?.rel_type === "m.replace";
    const isRedacted = ev.type === "m.room.redaction" || ev.unsigned?.redacted_because;

    let body = content.body || "";
    if (isEdit && content["m.new_content"]) {
      body = content["m.new_content"].body || body;
    }
    if (isRedacted) {
      body = "[Message deleted]";
    }

    // Extract reply info
    const replyTo = content["m.relates_to"]?.["m.in_reply_to"]?.event_id;

    // Extract attachments
    const attachments: MatrixAttachment[] = [];
    const msgtype = content.msgtype;
    if (msgtype === "m.image" || msgtype === "m.file" || msgtype === "m.video" || msgtype === "m.audio") {
      attachments.push({
        url: content.url || "",
        filename: content.body || "file",
        mimetype: content.info?.mimetype || "application/octet-stream",
        size: content.info?.size,
        width: content.info?.w,
        height: content.info?.h,
        thumbnailUrl: content.info?.thumbnail_url,
      });
    }

    return {
      eventId: ev.event_id || "",
      roomId: roomId || ev.room_id || "",
      sender: ev.sender || "",
      senderDisplayName: ev.unsigned?.["m.sender_display_name"] || ev.sender || "",
      senderAvatar: ev.unsigned?.["m.sender_avatar_url"] || undefined,
      content: body,
      timestamp: ev.origin_server_ts || Date.now(),
      type: ev.type || "m.room.message",
      edited: isEdit,
      redacted: isRedacted,
      replyTo,
      attachments: attachments.length > 0 ? attachments : undefined,
      msgtype,
    };
  }
}

// ==================== BACKEND PROXY CLIENT ====================

class BackendProxyClient implements IMatrixClient {
  readonly mode = "backend" as const;
  private _userId: string | null = null;

  // ── Auth ─────────────────────────────────────

  async login(user: string, _password: string): Promise<MatrixTokenResponse> {
    const { data } = await API.post(`matrix/token`, { userId: user });
    this._userId = data.matrixUserId || user;
    return data;
  }

  logout(): void {
    this._userId = null;
  }

  isLoggedIn(): boolean {
    return true;
  }

  getUserId(): string | null {
    return this._userId;
  }

  getAccessToken(): string | null {
    return null;
  }

  // ── Rooms ────────────────────────────────────

  async createRoom(params: {
    name: string;
    description?: string;
    image?: string;
    memberIds?: string[];
  }): Promise<string> {
    const payload = {
      name: params.name,
      description: params.description,
      image: params.image,
      avatarUrl: params.image,
      memberIds: params.memberIds,
    };
    const { data } = await API.post(`matrix/room`, payload);
    return data.roomId || data.id || data;
  }

  async createClubChat(params: CreateMatrixRoomParams): Promise<string> {
    if (!params.clubId) throw new Error("clubId required");
    const payload = {
      clubId: params.clubId,
      name: params.name,
      description: params.description,
      image: params.image,
      avatarUrl: params.image,
      memberIds: params.memberIds,
      memberIDs: params.memberIds,
      includeAll: params.includeAll,
      chatPurpose: params.chatPurpose || "CLUB",
    };
    const { data } = await API.post(`matrix/club_chat`, payload);
    return data.roomId || data.id || data;
  }

  async getRoomInfo(roomId: string): Promise<MatrixRoomInfo> {
    const { data } = await API.get(`matrix/room/${roomId}`);
    return {
      id: data.id || data.roomId || roomId,
      name: data.name || roomId,
      description: data.topic || data.description,
      image: data.avatarUrl || data.image,
      memberCount: data.memberCount,
      chatPurpose: data.chatPurpose,
      clubId: data.clubId,
    };
  }

  async deleteRoom(chatId: string): Promise<void> {
    await API.delete(`matrix/club_chat/${chatId}`);
  }

  async updateRoomName(roomId: string, name: string): Promise<void> {
    await API.put(`matrix/room/${roomId}/name`, { name });
  }

  async updateRoomDescription(roomId: string, description: string): Promise<void> {
    await API.put(`matrix/room/${roomId}/description`, { description });
  }

  async updateRoomAvatar(roomId: string, imageUrl: string): Promise<void> {
    await API.put(`matrix/room/${roomId}/avatar`, { avatarUrl: imageUrl, image: imageUrl, url: imageUrl });
  }

  async getPrivateChats(): Promise<MatrixRoomInfo[]> {
    const { data } = await API.get(`matrix/private_chats`);
    return (data || []).map((r: any) => ({
      ...r,
      id: r.roomId || r.id,
      image: r.avatarUrl || r.image,
      description: r.topic || r.description,
    }));
  }

  async getClubChats(clubId: string): Promise<MatrixRoomInfo[]> {
    const { data } = await API.get(`matrix/club/${clubId}/club_chats`);
    return (data || []).map((r: any) => ({
      ...r,
      // Use matrixRoomId for all room operations (Synapse API)
      id: r.matrixRoomId || r.id,
      bridgesChatId: r.id,
      image: r.avatarUrl || r.image,
      description: r.description || r.topic,
    }));
  }

  async getJoinedRooms(): Promise<MatrixRoomInfo[]> {
    const { data } = await API.get(`matrix/rooms`);
    return (data || []).map((r: any) => ({
      ...r,
      image: r.avatarUrl || r.image,
      description: r.topic || r.description,
    }));
  }

  // ── Members ──────────────────────────────────

  async addMember(roomId: string, userId: string): Promise<void> {
    await API.post(`matrix/room/${roomId}/member/${userId}`, {});
  }

  async removeMember(roomId: string, userId: string): Promise<void> {
    await API.delete(`matrix/room/${roomId}/member/${userId}`);
  }

  async getMembers(roomId: string): Promise<MatrixMember[]> {
    const { data } = await API.get(`matrix/room/${roomId}/members`);
    const raw = data?.result || data || [];
    return raw;
  }

  async getMemberProfiles(roomId: string): Promise<MatrixMemberProfile[]> {
    const { data } = await API.get(`matrix/room/${roomId}/member_profiles`);
    return data?.result || data || [];
  }

  async banUser(roomId: string, userId: string, reason?: string): Promise<void> {
    await API.post(`matrix/user/ban`, { targetUserId: userId, roomId, reason, ban: true });
  }

  async unbanUser(roomId: string, userId: string): Promise<void> {
    await API.post(`matrix/user/ban`, { targetUserId: userId, roomId, ban: false });
  }

  // ── Messages ─────────────────────────────────

  async sendMessage(roomId: string, content: string): Promise<string> {
    const { data } = await API.post(`matrix/room/${roomId}/message`, {
      content,
      messageType: "m.text",
    });
    return data.eventId || data.event_id || data;
  }

  async editMessage(roomId: string, eventId: string, newContent: string): Promise<string> {
    const { data } = await API.put(`matrix/room/${roomId}/message/${eventId}`, {
      content: newContent,
    });
    return data.eventId || data.event_id || data;
  }

  async deleteMessage(roomId: string, eventId: string, _reason?: string): Promise<string> {
    const { data } = await API.delete(`matrix/room/${roomId}/message/${eventId}`);
    return data.eventId || data.event_id || data || eventId;
  }

  async getMessages(
    roomId: string,
    limit: number = 50,
    from?: string
  ): Promise<{ messages: MatrixMessage[]; end?: string }> {
    const query = from ? `?limit=${limit}&from=${from}` : `?limit=${limit}`;
    const { data } = await API.get(`matrix/room/${roomId}/messages${query}`);
    const raw = data.messages || data || [];
    const messages: MatrixMessage[] = raw.map((m: any) => {
      const content = typeof m.content === "object" ? m.content : null;
      const body = content?.body || (typeof m.content === "string" ? m.content : "");
      const msgtype = content?.msgtype || m.msgtype;

      // Extract attachments for media messages
      let attachments: any[] | undefined;
      if (content?.url && (msgtype === "m.image" || msgtype === "m.video" || msgtype === "m.audio" || msgtype === "m.file")) {
        attachments = [{
          url: content.url,
          filename: content.body || "file",
          mimetype: content.info?.mimetype || "application/octet-stream",
          size: content.info?.size,
          width: content.info?.w,
          height: content.info?.h,
          thumbnailUrl: content.info?.thumbnail_url,
        }];
      }

      return {
        eventId: m.event_id || m.eventId || "",
        roomId: m.room_id || m.roomId || roomId,
        sender: m.sender || "",
        senderDisplayName: m.senderDisplayName || m.sender || "",
        content: body,
        msgtype,
        timestamp: m.timestamp || m.origin_server_ts || 0,
        type: m.type || "m.room.message",
        attachments,
      };
    });
    return { messages, end: data.end };
  }

  // ── Replies ──────────────────────────────────

  async sendReply(roomId: string, parentEventId: string, content: string): Promise<string> {
    const { data } = await API.post(`matrix/room/${roomId}/message`, {
      content,
      messageType: "m.text",
      replyToEventId: parentEventId,
    });
    return data.eventId || data.event_id || data;
  }

  // ── Reactions ────────────────────────────────

  async sendReaction(roomId: string, eventId: string, emoji: string): Promise<string> {
    try {
      const { data } = await API.post(`matrix/room/${roomId}/reaction`, {
        eventId,
        emoji,
      });
      return data.eventId || data.event_id || data;
    } catch {
      // Fallback: not supported in backend yet
      return "";
    }
  }

  async removeReaction(roomId: string, reactionEventId: string): Promise<string> {
    try {
      const { data } = await API.delete(`matrix/room/${roomId}/reaction/${reactionEventId}`);
      return data.eventId || data.event_id || data || reactionEventId;
    } catch {
      return "";
    }
  }

  // ── Pins ─────────────────────────────────────

  async pinMessage(roomId: string, eventId: string): Promise<void> {
    try {
      await API.post(`matrix/room/${roomId}/pin/${eventId}`, {});
    } catch {
      // Not implemented in backend yet
    }
  }

  async unpinMessage(roomId: string, eventId: string): Promise<void> {
    try {
      await API.delete(`matrix/room/${roomId}/pin/${eventId}`);
    } catch {
      // Not implemented in backend yet
    }
  }

  async getPinnedMessages(roomId: string): Promise<string[]> {
    try {
      const { data } = await API.get(`matrix/room/${roomId}/pins`);
      return data || [];
    } catch {
      return [];
    }
  }

  // ── File Upload ──────────────────────────────

  async uploadFile(file: File): Promise<string> {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await API.multipart(`matrix/upload`, formData, () => {});
    return data.mxcUrl || data.content_uri || data.url || "";
  }

  async sendFileMessage(roomId: string, file: File, mxcUrl: string): Promise<string> {
    let msgtype = "m.file";
    if (file.type.startsWith("image/")) msgtype = "m.image";
    else if (file.type.startsWith("video/")) msgtype = "m.video";
    else if (file.type.startsWith("audio/")) msgtype = "m.audio";

    const { data } = await API.post(`matrix/room/${roomId}/message`, {
      content: file.name,
      messageType: msgtype,
      url: mxcUrl,
      filename: file.name,
      mimetype: file.type,
      size: file.size,
    });
    return data.eventId || data.event_id || data;
  }

  getMediaUrl(mxcUrl: string): string {
    if (!mxcUrl || !mxcUrl.startsWith("mxc://")) return mxcUrl;
    const parts = mxcUrl.replace("mxc://", "").split("/");
    const serverName = parts[0];
    const mediaId = parts.slice(1).join("/");
    const apiBase = process.env.REACT_APP_API || "";
    return `${apiBase}/api/v1/matrix/media/${serverName}/${mediaId}`;
  }

  // ── Forward ──────────────────────────────────

  async forwardMessage(_fromRoomId: string, toRoomId: string, content: string): Promise<string> {
    return this.sendMessage(toRoomId, content);
  }

  // ── Real-time ────────────────────────────────

  async sync(_since?: string, _timeout?: number): Promise<MatrixSyncResponse> {
    return { nextBatch: "", rooms: { join: {}, invite: {}, leave: {} } };
  }

  async sendTypingIndicator(roomId: string, isTyping: boolean): Promise<void> {
    try {
      await API.post(`matrix/room/${roomId}/typing`, { typing: isTyping });
    } catch { /* non-critical */ }
  }

  async sendReadReceipt(roomId: string, eventId: string): Promise<void> {
    try {
      await API.post(`matrix/room/${roomId}/read/${eventId}`, {});
    } catch { /* non-critical */ }
  }

  async getUnreadCounts(): Promise<MatrixUnreadCounts> {
    try {
      const { data } = await API.get(`matrix/unread`);
      return data.result || data || {};
    } catch {
      return {};
    }
  }

  // ── Status ───────────────────────────────────

  async getStatus(): Promise<any> {
    const { data } = await API.get(`matrix/status`);
    return data;
  }
}

// ==================== FACTORY ====================

const MATRIX_HOMESERVER = process.env.REACT_APP_MATRIX_HOMESERVER || "http://localhost:8008";

export function createMatrixClient(
  mode: "direct" | "backend" = resolveMatrixMode(),
  homeserver: string = MATRIX_HOMESERVER,
): IMatrixClient {
  if (mode === "direct") {
    console.log(`[Matrix] Direct mode → ${homeserver}`);
    return new SynapseDirectClient(homeserver);
  }
  console.log("[Matrix] Backend proxy mode");
  return new BackendProxyClient();
}

export const matrixClient: IMatrixClient = createMatrixClient();

export default matrixClient;
